import { useState, useEffect, useRef, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MyContext } from '../../Context/MyProvider';
import secureLocalStorage from 'react-secure-storage';
import axios from 'axios';
import io from 'socket.io-client';

const ChatWidget = () => {
    const { BaseUrl, supportChatStatus, isLoginAuth } = useContext(MyContext);
    
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isAdminOnline, setIsAdminOnline] = useState(false);
    const [isSessionClosed, setIsSessionClosed] = useState(false);
    const [chatbotSettings, setChatbotSettings] = useState({
        fallbackReply: "Thank you for your message! Our support agents are currently offline, but we have received your query. A human agent will reply as soon as they log back in.",
        topics: [
            { label: "💳 Plans & Payments", question: "How to purchase packages?" },
            { label: "🌌 Calculations", question: "Astrology calculation issues?" },
            { label: "⏰ Support Hours", question: "When is support active?" }
        ]
    });
    
    const socketRef = useRef(null);
    const messagesEndRef = useRef(null);

    // Scroll to bottom when messages list updates
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen]);

    // Initialize session and socket connection when chat box is opened
    useEffect(() => {
        if (!isOpen) {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }
            setSessionId(null);
            setMessages([]);
            setIsSessionClosed(false);
            return;
        }

        const LocalData = secureLocalStorage.getItem("Localdata");
        if (!LocalData) return;
        
        let user;
        try {
            user = JSON.parse(LocalData);
        } catch (e) {
            console.error("Failed to parse Localdata:", e);
            return;
        }

        // 1. Initialize/Retrieve support chat session
        axios.post(`${BaseUrl}/support-chat/init`, { userId: user.id })
            .then((res) => {
                if (res.data.status === 'success') {
                    const sessId = res.data.sessionId;
                    setSessionId(sessId);
                    setIsAdminOnline(!!res.data.isAdminOnline);
                    if (res.data.chatbotSettings) {
                        setChatbotSettings(res.data.chatbotSettings);
                    }

                    // 2. Connect to Socket.io server (extract host port 5001 from API URL)
                    const socketUrl = new URL(BaseUrl).origin;
                    socketRef.current = io(socketUrl);

                    // Join specific session room
                    socketRef.current.emit('join_session', { sessionId: sessId });

                    // Listen for real-time admin status broadcasts
                    socketRef.current.on('admin_status', ({ online }) => {
                        setIsAdminOnline(online);
                    });

                    // 3. Load message history
                    axios.get(`${BaseUrl}/support-chat/messages/${sessId}`)
                        .then((mRes) => {
                            if (mRes.data.status === 'success') {
                                setMessages(mRes.data.messages);
                            }
                        })
                        .catch((err) => console.error("Error loading chat messages:", err));

                    // 4. Socket message broadcast receiver
                    socketRef.current.on('message_received', (msg) => {
                        setMessages((prev) => [...prev, msg]);
                    });

                    // Listen for session closed event from server
                    socketRef.current.on('session_closed', () => {
                        setIsSessionClosed(true);
                    });
                }
            })
            .catch((err) => {
                console.error("Error initializing support chat session:", err);
            });

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [isOpen, BaseUrl]);

    // Send Message
    const handleSendMessage = (e) => {
        e.preventDefault();
        const messageText = inputValue.trim();
        if (!messageText || !sessionId || !socketRef.current) return;

        socketRef.current.emit('send_message', {
            sessionId: sessionId,
            sender: 'user',
            message: messageText
        });

        setInputValue("");
    };

    // Send quick reply Q&A topic suggestion
    const handleSendSuggestion = (text) => {
        if (!sessionId || !socketRef.current) return;

        socketRef.current.emit('send_message', {
            sessionId: sessionId,
            sender: 'user',
            message: text
        });
    };

    // Start a new chat session when closed
    const handleStartNewChat = () => {
        setIsSessionClosed(false);
        setSessionId(null);
        setMessages([]);
        setIsOpen(false);
        setTimeout(() => {
            setIsOpen(true);
        }, 100);
    };

    // If chat feature is globally disabled or user is not logged in, render nothing
    if (supportChatStatus !== 1 || isLoginAuth) {
        return null;
    }

    return (
        <div className="support-chat-widget">
            {/* Chat Trigger Button */}
            <motion.div 
                className="support-chat-bubble"
                onClick={() => setIsOpen(!isOpen)}
                whileTap={{ scale: 0.9 }}
            >
                {isOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                )}
            </motion.div>

            {/* Chat Box Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div 
                        className="support-chat-window"
                        initial={{ opacity: 0, scale: 0.8, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 50 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    >
                        {/* Header */}
                        <div className="support-chat-header">
                            <div>
                                <span className="support-chat-title">Live Chat Support</span>
                                <span className="support-chat-subtitle">AstroSphere Support Team</span>
                            </div>
                            <button className="support-chat-close-btn" onClick={() => setIsOpen(false)}>×</button>
                        </div>

                        {/* Messages Area */}
                        <div className="support-chat-messages">
                            {messages.length === 0 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-400 p-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <p className="text-sm font-semibold text-gray-600">Start a chat</p>
                                    <p className="text-xs text-gray-500 mt-1">Our support team is online to help you.</p>
                                </div>
                            ) : (
                                messages.map((msg, index) => {
                                    const isUser = msg.sender === 'user';
                                    const isBot = msg.sender === 'bot';
                                    let rowClass = 'admin-msg';
                                    if (isUser) {
                                        rowClass = 'user-msg';
                                    } else if (isBot) {
                                        rowClass = 'bot-msg';
                                    }
                                    return (
                                        <div key={index} className={`support-message-row ${rowClass}`}>
                                            <div className="support-msg-bubble">
                                                {isBot && (
                                                    <span className="bot-indicator">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                            <path d="M12 2v2"></path>
                                                            <path d="M12 7a5 5 0 0 1 5 5"></path>
                                                            <path d="M12 7a5 5 0 0 0-5 5"></path>
                                                        </svg>
                                                        Bot
                                                    </span>
                                                )}
                                                {msg.message}
                                            </div>
                                            <span className="support-msg-time">
                                                {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Q&A Suggestions for Offline Bot */}
                        {!isAdminOnline && !isSessionClosed && chatbotSettings.topics && chatbotSettings.topics.length > 0 && (
                            <div className="support-chat-suggestions">
                                {chatbotSettings.topics.map((topic, i) => {
                                    if (!topic.label || !topic.question) return null;
                                    return (
                                        <button 
                                            key={i} 
                                            type="button" 
                                            className="support-chat-suggest-btn" 
                                            onClick={() => handleSendSuggestion(topic.question)}
                                        >
                                            {topic.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Input Area or Closed Banner */}
                        {isSessionClosed ? (
                            <div className="support-chat-closed-banner">
                                <span>Support session closed.</span>
                                <button type="button" className="support-chat-new-btn" onClick={handleStartNewChat}>
                                    Start New Chat
                                </button>
                            </div>
                        ) : (
                            <form className="support-chat-footer" onSubmit={handleSendMessage}>
                                <input 
                                    type="text"
                                    className="support-chat-input"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder="Type your message..."
                                    required
                                    autoComplete="off"
                                />
                                <button type="submit" className="support-chat-send">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                    </svg>
                                </button>
                            </form>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ChatWidget;
